import ButtonT from '@components/tailwind-components/Button/ButtonT';
import InputT from '@components/tailwind-components/Input/InputT';
import LabelT from '@components/tailwind-components/Label/LabelT';
import { useModal } from '@hooks/modal/useModal';
import type React from 'react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

const MAX_KEYWORD_LENGTH = 50;

type Props = {
  onSubmit: (keyword: string) => void;
  defaultValue?: string;
  existingKeywords: string[];
};

export const KeywordDataModal: React.FC<Props> = ({
  onSubmit,
  defaultValue = '',
  existingKeywords,
}) => {
  const { t } = useTranslation();
  const { hideModal } = useModal();
  const [keyword, setKeyword] = useState(defaultValue);
  const [keywordError, setKeywordError] = useState('');

  const validateKeyword = useCallback(
    (value: string): string => {
      const trimmed = value.trim();

      if (value.length > 0 && trimmed.length === 0) {
        return t('train-ai.keywords.error.whitespace-only');
      }

      if (trimmed.length > MAX_KEYWORD_LENGTH) {
        return t('train-ai.keywords.error.max-length', {
          max: MAX_KEYWORD_LENGTH,
        });
      }

      if (existingKeywords.includes(trimmed)) {
        return t('train-ai.keywords.error.duplicate');
      }

      return '';
    },
    [t, existingKeywords],
  );

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setKeyword(value);
      setKeywordError(validateKeyword(value));
    },
    [validateKeyword],
  );

  const handleSubmit = useCallback(() => {
    const trimmed = keyword.trim();

    if (!trimmed) {
      setKeywordError(t('general.validation.required'));
      return;
    }

    const error = validateKeyword(keyword);

    if (error) {
      setKeywordError(error);
      return;
    }

    onSubmit(trimmed);
    hideModal();
  }, [keyword, validateKeyword, onSubmit, hideModal, t]);

  return (
    <div className='tailwind-modal-form'>
      <div className='tailwind-modal-body'>
        <div className='grid grid-cols-1 gap-x-6 gap-y-2'>
          <div className='col-span-1'>
            <LabelT
              text={t('conversation-keywords-settings.keyword-label')}
              className='mb-1'
            />
            <InputT
              type='text'
              placeholder={t(
                'conversation-keywords-settings.keyword-placeholder',
              )}
              error={keywordError}
              options={{
                value: keyword,
                onChange: handleChange,
              }}
            />
          </div>
        </div>
      </div>
      <div className='tailwind-modal-footer'>
        <ButtonT
          type='button'
          styling='cancel'
          size='xs'
          rounding='md'
          onClick={hideModal}>
          {t('general.buttons.cancel')}
        </ButtonT>
        <ButtonT
          type='button'
          styling='primary'
          size='xs'
          rounding='md'
          onClick={handleSubmit}
          disabled={!!keywordError}>
          {t('general.buttons.save')}
        </ButtonT>
      </div>
    </div>
  );
};

export default KeywordDataModal;
